import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { statsApi, goalsApi, textsApi } from '../utils/api';
import { Clock, AlertTriangle, Bookmark, BookOpen, RefreshCw, Settings2, Bell, History } from 'lucide-react';
import { SESSION_KEY, readSession, writeSession } from './FloatingStudyTimer';

const BUDGET_KEY = 'iling_study_plan_budget';
const ASSESS_KEY = 'iling_priority_assessment_v1';
const PLAN_CACHE_KEY = 'iling_study_plan_cache';
const PLAN_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

function loadCachedPlan() {
  try {
    const raw = localStorage.getItem(PLAN_CACHE_KEY);
    if (!raw) return null;
    const { plan, generated_at } = JSON.parse(raw);
    if (Date.now() - generated_at > PLAN_TTL_MS) { localStorage.removeItem(PLAN_CACHE_KEY); return null; }
    return { plan, generated_at };
  } catch { return null; }
}

function saveCachedPlan(plan) {
  try { localStorage.setItem(PLAN_CACHE_KEY, JSON.stringify({ plan, generated_at: Date.now() })); } catch {}
}

function getStaleLanguages(languageNames) {
  try {
    const data = JSON.parse(localStorage.getItem(ASSESS_KEY)) || {};
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const stale = [];
    for (const [langId, info] of Object.entries(data)) {
      if (info.status === 'completed' && info.completed_at) {
        if (new Date(info.completed_at).getTime() < thirtyDaysAgo) {
          const name = languageNames?.[langId];
          stale.push(name || langId);
        }
      }
    }
    return stale;
  } catch { return []; }
}

// ─── History helpers ─────────────────────────────────────────────────────────

const HISTORY_KEY = 'iling_study_plan_history';

function saveToHistory(plan, budgetMinutes, streakOk) {
  try {
    const today = new Date().toISOString().slice(0, 10);
    const entry = {
      date: today,
      budget_minutes: budgetMinutes,
      total_allocated: (plan.primary_plan || []).reduce((s, p) => s + p.suggested_minutes, 0),
      languages: (plan.primary_plan || []).map(p => ({
        name: p.name, minutes: p.suggested_minutes, tier: p.tier,
      })),
      streak_ok: streakOk,  // true = activity logged that day, false = no activity, null = unknown
    };
    const saved = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    const rest = saved.filter(e => e.date !== today);
    localStorage.setItem(HISTORY_KEY, JSON.stringify([entry, ...rest].slice(0, 7)));
  } catch {}
}

function loadHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); } catch { return []; }
}

// Returns a suggested budget (minutes) when ≥ 3 history entries exist, else null
function getAdaptiveSuggestion(history) {
  if (history.length < 3) return null;
  const avg = Math.round(history.reduce((s, e) => s + e.budget_minutes, 0) / history.length);
  return avg;
}

function fmtDate(dateStr) {
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (dateStr === today) return 'Today';
  if (dateStr === yesterday) return 'Yesterday';
  return new Date(dateStr + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

const TIER_COLORS = { priority: '#EF4444', maintenance: '#F59E0B', optional: '#6B7280' };

function HistoryRow({ entry }) {
  return (
    <div className="space-y-1" data-testid={`history-row-${entry.date}`}>
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5">
          {/* Streak indicator dot */}
          {entry.streak_ok === true && (
            <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" title="Streak maintained" data-testid="streak-dot-maintained" />
          )}
          {entry.streak_ok === false && (
            <span className="h-2 w-2 rounded-full bg-muted-foreground/30 shrink-0" title="No activity logged" data-testid="streak-dot-broken" />
          )}
          <span className="font-medium">{fmtDate(entry.date)}</span>
        </div>
        <span className="text-muted-foreground tabular-nums">
          {entry.total_allocated} / {entry.budget_minutes} min
        </span>
      </div>
      {/* Stacked proportional bar */}
      {entry.languages.length > 0 && (
        <div className="flex h-2 w-full rounded-full overflow-hidden gap-px bg-muted">
          {entry.languages.map((lang, i) => {
            const pct = (lang.minutes / entry.budget_minutes) * 100;
            return pct > 0 ? (
              <div
                key={i}
                className="h-full first:rounded-l-full last:rounded-r-full"
                style={{ width: `${pct}%`, backgroundColor: TIER_COLORS[lang.tier] || TIER_COLORS.optional, minWidth: '2px' }}
                title={`${lang.name}: ${lang.minutes} min`}
              />
            ) : null;
          })}
        </div>
      )}
      {/* Language chips */}
      <div className="flex flex-wrap gap-x-2 gap-y-0.5">
        {entry.languages.map((lang, i) => (
          <span key={i} className="text-[10px] text-muted-foreground/70">
            {lang.name} {lang.minutes}m
          </span>
        ))}
      </div>
    </div>
  );
}

// Tier styling config
const TIER_CONFIG = {
  priority: {
    label: 'Critical',
    bar: '#EF4444',
    badge: 'bg-red-500/15 text-red-400 border-red-500/30',
  },
  maintenance: {
    label: 'Maintenance',
    bar: '#F59E0B',
    badge: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  },
  optional: {
    label: 'Optional',
    bar: '#6B7280',
    badge: 'bg-muted/40 text-muted-foreground border-border',
  },
};

function PlanRow({ item, maxMinutes, onStart, isVisited }) {
  const tier = TIER_CONFIG[item.tier] || TIER_CONFIG.optional;
  const barPct = Math.min(100, (item.suggested_minutes / maxMinutes) * 100);
  return (
    <div
      className={`space-y-1 rounded-lg transition-all duration-200 ${isVisited ? 'opacity-40 select-none' : 'cursor-pointer hover:bg-muted/30 -mx-1 px-1 py-1'}`}
      onClick={!isVisited ? onStart : undefined}
      title={isVisited ? 'Already visited this session' : `Start ${item.name}`}
      data-testid={`plan-row-${item.language_code}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${tier.badge}`}>
            {tier.label}
          </span>
          <span className="text-sm font-medium truncate">{item.name}</span>
          {item.has_assessment && (
            <span
              className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20"
              title="Priority score from your assessment is influencing this allocation"
              data-testid={`assessed-badge-${item.language_code}`}
            >
              ★ Assessed
            </span>
          )}
        </div>
        <span className="shrink-0 text-sm font-semibold tabular-nums">{item.suggested_minutes} min</span>
      </div>
      <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${barPct}%`, backgroundColor: tier.bar }} />
      </div>
      <p className="text-[11px] text-muted-foreground">{item.explanation}</p>
    </div>
  );
}

export function StudyPlanWidget() {
  const navigate = useNavigate();
  const savedBudget = localStorage.getItem(BUDGET_KEY);
  const cached = loadCachedPlan();
  const [budgetInput, setBudgetInput] = useState(savedBudget || '30');
  const [plan, setPlan] = useState(cached?.plan || null);
  const [planGeneratedAt, setPlanGeneratedAt] = useState(cached?.generated_at || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [staleLanguages, setStaleLanguages] = useState([]);
  const [history, setHistory] = useState(loadHistory);
  const [showHistory, setShowHistory] = useState(false);
  const [visitedIds, setVisitedIds] = useState(() => {
    const s = readSession();
    if (!s) return new Set();
    const ids = new Set((s.languages || []).filter(l => l.visited).map(l => l.language_id));
    // Also mark the language currently being studied as disabled
    const cur = s.languages?.[s.current_index];
    if (cur) ids.add(cur.language_id);
    return ids;
  });

  // Keep visitedIds in sync when FloatingStudyTimer marks languages as visited
  useEffect(() => {
    const handler = () => {
      const s = readSession();
      if (!s) { setVisitedIds(new Set()); return; }
      const ids = new Set((s.languages || []).filter(l => l.visited).map(l => l.language_id));
      const cur = s.languages?.[s.current_index];
      if (cur) ids.add(cur.language_id);
      setVisitedIds(ids);
    };
    window.addEventListener('study-session-updated', handler);
    return () => window.removeEventListener('study-session-updated', handler);
  }, []);

  const handleGenerate = async () => {
    const mins = parseInt(budgetInput, 10);
    if (!mins || mins < 1) return;
    localStorage.setItem(BUDGET_KEY, String(mins));
    setLoading(true);
    setError(null);
    // Clear any existing session — new plan invalidates prior progress
    localStorage.removeItem(SESSION_KEY);
    window.dispatchEvent(new CustomEvent('study-session-updated'));
    setVisitedIds(new Set());
    try {
      const res = await statsApi.getStudyPlan(mins);
      setPlan(res.data);
      saveCachedPlan(res.data);
      setPlanGeneratedAt(Date.now());
      // Fetch streak status for today's history entry (best-effort)
      let streakOk = null;
      try {
        const sr = await goalsApi.getStreak();
        streakOk = sr.data?.has_activity_today === true;
      } catch {}
      saveToHistory(res.data, mins, streakOk);
      setHistory(loadHistory());
      // Build a name map from the plan and check for stale assessments
      const nameMap = {};
      [...(res.data.primary_plan || []), ...(res.data.backup_plan || [])].forEach(l => {
        nameMap[l.language_id] = l.name;
      });
      setStaleLanguages(getStaleLanguages(nameMap));
    } catch {
      setError('Could not load study plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleGenerate(); };

  const handleStartLanguage = async (item) => {
    const allPrimary = plan?.primary_plan || [];
    const languages = allPrimary.map(l => ({
      language_id: l.language_id,
      language_code: l.language_code,
      name: l.name,
      minutes: l.suggested_minutes,
      visited: visitedIds.has(l.language_id),
    }));
    const currentIndex = languages.findIndex(l => l.language_id === item.language_id);
    if (currentIndex === -1) return;
    languages[currentIndex].visited = false;
    writeSession({ languages, current_index: currentIndex });

    // Navigate to the first available text for this language, else fall back to texts list
    try {
      const res = await textsApi.getAll({ language_id: item.language_id, limit: 1 });
      const texts = res.data || [];
      if (texts.length > 0) {
        const textId = texts[0].id || texts[0].text_id;
        navigate(`/reader/${textId}`);
      } else {
        navigate('/texts');
      }
    } catch {
      navigate('/texts');
    }
  };

  const allItems = [...(plan?.primary_plan || []), ...(plan?.backup_plan || [])];
  const maxMinutes = allItems.length ? Math.max(...allItems.map(p => p.suggested_minutes), 1) : 1;

  return (
    <Card data-testid="study-plan-widget">
      <CardContent className="pt-5 pb-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <span className="font-semibold">Study Plan</span>
          </div>
          <Link to="/language-priority" data-testid="priority-level-btn">
            <Button variant="ghost" size="sm" className="gap-1.5 h-7 text-xs text-muted-foreground hover:text-foreground">
              <Settings2 className="h-3.5 w-3.5" />
              Priority Level
            </Button>
          </Link>
        </div>

        {/* Adaptive suggestion */}
        {(() => {
          const suggestion = getAdaptiveSuggestion(history);
          const current = parseInt(budgetInput, 10);
          if (!suggestion || Math.abs(suggestion - current) < 3) return null;
          return (
            <p className="text-xs text-muted-foreground mb-2">
              Based on your last {history.length} sessions, your average is{' '}
              <button
                onClick={() => setBudgetInput(String(suggestion))}
                className="font-semibold text-primary underline underline-offset-2 hover:no-underline"
                data-testid="adaptive-suggestion-btn"
              >
                {suggestion} min
              </button>
              {' '}— tap to use it.
            </p>
          );
        })()}

        {/* Time input */}
        <div className="flex items-center gap-2 mb-4">
          <Input
            type="number"
            min={1}
            max={480}
            value={budgetInput}
            onChange={(e) => setBudgetInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Minutes available"
            className="h-9 text-sm w-28"
            data-testid="study-plan-minutes-input"
          />
          <span className="text-sm text-muted-foreground shrink-0">min available</span>
          <Button
            size="sm"
            onClick={handleGenerate}
            disabled={loading || !budgetInput}
            className="ml-auto"
            data-testid="study-plan-generate-btn"
          >
            {loading ? 'Planning…' : 'Plan'}
          </Button>
        </div>

        {error && <p className="text-sm text-destructive mb-3">{error}</p>}

        {plan && (
          <>
            {planGeneratedAt && (
              <p className="text-xs text-muted-foreground mb-2">
                Plan from {new Date(planGeneratedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {' '}· refreshes after{' '}
                {new Date(planGeneratedAt + PLAN_TTL_MS).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
            {plan.time_is_tight && (
              <div className="flex items-start gap-2 mb-3 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-300 leading-snug">
                  Time is short. Critical items are prioritised — the rest are in your Backup Plan.
                </p>
              </div>
            )}

            {staleLanguages.length > 0 && (
              <div className="flex items-start gap-2 mb-3 p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <Bell className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-blue-300 leading-snug">
                    Assessment for <strong>{staleLanguages.join(', ')}</strong> is over 30 days old. Retake for a more accurate plan.
                  </p>
                </div>
                <Link to="/language-priority" className="shrink-0">
                  <button className="text-[10px] text-blue-400 hover:text-blue-300 underline">Update</button>
                </Link>
              </div>
            )}

            {/* Primary plan */}
            {plan.primary_plan?.length > 0 ? (
              <div className="space-y-3 mb-3">
                {plan.primary_plan.map((item) => (
                  <PlanRow key={item.language_id} item={item} maxMinutes={maxMinutes} onStart={() => handleStartLanguage(item)} isVisited={visitedIds.has(item.language_id)} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-3">No active languages found.</p>
            )}

            {/* Total */}
            {plan.primary_plan?.length > 0 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border/50 pt-2 mb-3">
                <span>Primary plan total</span>
                <span className="font-semibold text-foreground">
                  {plan.primary_plan.reduce((s, p) => s + p.suggested_minutes, 0)} / {plan.budget_minutes} min
                </span>
              </div>
            )}

            {/* Backup plan */}
            {plan.backup_plan?.length > 0 && (
              <div className="mt-1">
                <div className="flex items-center gap-1.5 mb-2">
                  <Bookmark className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Backup Plan</span>
                </div>
                <div className="space-y-2.5 pl-1 border-l-2 border-muted">
                  {plan.backup_plan.map((item) => (
                    <div key={item.language_id} className="pl-2" data-testid={`backup-row-${item.language_code}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-muted-foreground">{item.name}</span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs text-muted-foreground tabular-nums">~{item.suggested_minutes} min</span>
                          <Link to="/review" title={`Review ${item.name}`}>
                            <button className="p-1 rounded hover:bg-muted transition-colors" data-testid={`backup-review-${item.language_code}`}>
                              <RefreshCw className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                            </button>
                          </Link>
                          <Link to="/texts" title={`Read ${item.name}`}>
                            <button className="p-1 rounded hover:bg-muted transition-colors" data-testid={`backup-read-${item.language_code}`}>
                              <BookOpen className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                            </button>
                          </Link>
                        </div>
                      </div>
                      {item.backup_reason && (
                        <p className="text-[11px] text-muted-foreground/70 mt-0.5">{item.backup_reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {!plan && !loading && (
          <p className="text-sm text-muted-foreground text-center py-3">
            Enter how many minutes you have and tap <strong>Plan</strong> to get a personalised schedule.
          </p>
        )}

        {/* 7-Day History */}
        {history.length > 0 && (
          <div className="mt-3 border-t border-border/50 pt-3">
            <button
              onClick={() => setShowHistory(h => !h)}
              className="flex items-center justify-between w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="history-toggle-btn"
            >
              <div className="flex items-center gap-1.5">
                <History className="h-3.5 w-3.5" />
                <span className="font-semibold uppercase tracking-wide">7-Day History</span>
              </div>
              <span className="text-[10px]">{showHistory ? '▲' : '▼'}</span>
            </button>
            {showHistory && (
              <div className="mt-3 space-y-4" data-testid="history-list">
                {history.map(entry => (
                  <HistoryRow key={entry.date} entry={entry} />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
